import { useCallback, useEffect, useState } from 'react';
import { getUserDetails, makeGetCall, makePostCall } from '../../../utils/helper';
import type { Report, OrderDetails, PatientNoteGroup, RadUser } from '../types';

/**
 * Fetches all data needed by the report editor for a given order:
 *  - reports (for this order)
 *  - prior reports (from other orders for same patient)
 *  - patient notes / documents
 *  - radiologist users (for co-sign)
 *  - all users (for proxy draft)
 *
 * Returns stable callbacks for refetching after mutations.
 */
export const useReportData = (orderDetails: OrderDetails | null) => {
  const [reports, setReports] = useState<Report[]>([]);
  const [currentReport, setCurrentReport] = useState<Report | null>(null);
  const [priorReports, setPriorReports] = useState<Report[]>([]);
  const [notes, setNotes] = useState<PatientNoteGroup[]>([]);
  const [radUsers, setRadUsers] = useState<RadUser[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [isSignedOff, setIsSignedOff] = useState(false);

  const pacs_order = orderDetails?.pacs_order;
  const patient = pacs_order?.patient;

  // ── Reports for this order ──
  const fetchReports = useCallback(async () => {
    if (!pacs_order) return;
    try {
      const res = await makePostCall('/get-reports', { order_id: pacs_order.pacs_ord_id });
      const data: Report[] = res.data?.data || [];
      setReports(data);

      const hasSignedOff = data.some(r => r.pr_status === 'SIGNEDOFF');
      setIsSignedOff(hasSignedOff);

      if (data.length > 0) {
        const updated = data.find(r => r.pr_id === currentReport?.pr_id) || data[0];
        setCurrentReport(updated);
      } else {
        setCurrentReport({ pr_report_html: '<div></div>' });
      }
    } catch (e) {
      console.error('Failed to fetch reports:', e);
    }
  }, [pacs_order?.pacs_ord_id]);

  // ── Prior reports (other orders for same patient) ──
  const fetchPriorReports = useCallback(async () => {
    if (!pacs_order || !patient) return;
    try {
      const res = await makePostCall('/get-prior-reports', {
        yh_no: patient.pat_pin,
        his_ord_no: pacs_order.po_his_ord_no,
        acc_no: pacs_order.po_acc_no,
        order_id: pacs_order.pacs_ord_id,
      });
      setPriorReports(res.data?.data || []);
    } catch {
      setPriorReports([]);
    }
  }, [pacs_order?.pacs_ord_id, patient?.pat_pin]);

  // ── Patient notes / documents ──
  const fetchNotes = useCallback(async () => {
    if (!pacs_order || !patient) return;
    try {
      const res = await makePostCall('/pat-prev-notes', {
        user_id: getUserDetails()?.username,
        pin: patient.pat_pin,
        order_id: pacs_order.po_his_ord_no,
        ord_no: pacs_order.pacs_ord_id,
      });
      setNotes(res.data?.data || []);
    } catch {
      setNotes([]);
    }
  }, [pacs_order?.pacs_ord_id, patient?.pat_pin]);

  // ── Rad users (for co-sign dropdown) ──
  const fetchRadUsers = useCallback(async () => {
    try {
      const res = await makeGetCall('/get-rad-users');
      setRadUsers(res.data?.data || []);
    } catch {
      setRadUsers([]);
    }
  }, []);

  // ── All users (for proxy draft dropdown) ──
  const fetchAllUsers = useCallback(async () => {
    try {
      const res = await makePostCall('/user-list', {});
      setAllUsers(res.data?.data || []);
    } catch {
      setAllUsers([]);
    }
  }, []);

  // Fetch everything when order changes
  useEffect(() => {
    if (!orderDetails) return;
    fetchReports();
    fetchPriorReports();
    fetchNotes();
  }, [orderDetails]);

  // Fetch user lists once on mount
  useEffect(() => {
    fetchRadUsers();
    fetchAllUsers();
  }, []);

  return {
    reports,
    currentReport,
    setCurrentReport,
    priorReports,
    notes,
    radUsers,
    allUsers,
    isSignedOff,
    setIsSignedOff,
    refetchReports: fetchReports,
  };
};
